import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useEffect, useMemo, useState } from 'react';
import { useCompany } from '@/company/context';
import { subscribeKnowledgePagesChanged } from '../knowledge-agent-events';
import { errorStatus, knowledgePagesApi, pagesErrorMessage } from './api';
import { indexTree } from './tree';
import type { KnowledgeTree, PageHistoryEntry, PageProposal, PageView } from './types';

interface Loaded<T> {
  key: string;
  value: T | null;
  error: string;
  missing: boolean;
}

/**
 * One read that follows the company: its answer is stamped with the company
 * (and whatever else `key` names) so a switch never shows the previous
 * company's data, it is read again whenever a page or suggestion changes
 * anywhere in the workspace, and a failed reread keeps the last answer.
 */
function useCompanyRead<T>(
  key: string | null,
  read: () => Promise<T>,
  fallback: string,
): Loaded<T> & { retry: () => void; loading: boolean } {
  const companyId = useCompany().data?.company?.id ?? '';
  const locale = useChatInterfaceLocale();
  const scope = key === null || !companyId ? null : `${companyId}\0${key}`;
  const [state, setState] = useState<Loaded<T>>({
    key: scope ?? '',
    value: null,
    error: '',
    missing: false,
  });
  const [reload, setReload] = useState(0);
  useEffect(() => subscribeKnowledgePagesChanged(() => setReload((value) => value + 1)), []);
  useEffect(() => {
    if (!scope) return;
    let active = true;
    void read().then(
      (value) => {
        if (active) setState({ key: scope, value, error: '', missing: false });
      },
      (failure) => {
        if (!active) return;
        const status = errorStatus(failure);
        setState((previous) => ({
          key: scope,
          value: previous.key === scope ? previous.value : null,
          error: pagesErrorMessage(failure, fallback, locale),
          missing: status === 404 || status === 403,
        }));
      },
    );
    return () => {
      active = false;
    };
  }, [scope, reload]);
  const current =
    state.key === scope ? state : { key: scope ?? '', value: null, error: '', missing: false };
  return {
    ...current,
    loading: Boolean(scope) && current.value === null && !current.error,
    retry: () => setReload((value) => value + 1),
  };
}

/** The company's page tree (it creates the root page on first read). */
export function useKnowledgeTree(enabled = true) {
  const read = useCompanyRead<KnowledgeTree>(
    enabled ? 'tree' : null,
    () => knowledgePagesApi.tree(),
    'We could not load the knowledge pages.',
  );
  const index = useMemo(() => (read.value ? indexTree(read.value) : null), [read.value]);
  return { ...read, tree: read.value, index };
}

export type KnowledgeTreeState = ReturnType<typeof useKnowledgeTree>;

/** One page as it reads now, with its open suggestions, children and backlinks. */
export function usePageView(id: string | null) {
  const read = useCompanyRead<PageView>(
    id,
    () => knowledgePagesApi.view(id!),
    'We could not load this page.',
  );
  return { ...read, view: read.value };
}

/** The pending suggestions this person may see, for For review. */
export function usePendingProposals(enabled = true) {
  const read = useCompanyRead<PageProposal[]>(
    enabled ? 'proposals' : null,
    () => knowledgePagesApi.proposals(),
    'We could not load the suggestions waiting for review.',
  );
  return { ...read, proposals: read.value };
}

/** A page's revisions, newest first. */
export function usePageHistory(id: string) {
  const read = useCompanyRead<PageHistoryEntry[]>(
    `history\0${id}`,
    () => knowledgePagesApi.history(id),
    'We could not load the revision history.',
  );
  return { ...read, revisions: read.value };
}
